import { useEffect, useState } from 'react';
import { Form, Input, SingleColumnLayout, Box, FormGroup, P, Span } from '@salecycle/ui-component-library';
import moment, { Moment } from 'moment';

import { useTranslation } from 'react-i18next';
import { usePreferencesState } from 'core/preferences/usePreferencesContext';
import {
  useNewOffsiteCampaignState,
  useNewOffsiteCampaignDispatch,
} from '../../../context/UseNewOffsiteCampaignContext';
import { updateStepValidityAction } from '../../../actions';
import DateTimePicker from 'sections/offsite-campaigns/components/DateTimePicker';

export interface IProps {
  currentStep: number;
}

/* eslint-disable react-hooks/exhaustive-deps */
export const DetailsStep: React.FC<IProps> = ({ currentStep }: IProps) => {
  const dispatch = useNewOffsiteCampaignDispatch();
  const { t } = useTranslation(['campaigns', 'integrations']);
  const { timezone } = usePreferencesState();
  const { campaign, validSteps } = useNewOffsiteCampaignState();

  const startTime = (): number => {
    return campaign.startTime;
  };
  const endTime = (): number => {
    return campaign.endTime;
  };

  const name = (): string => {
    return campaign.name;
  };

  const startTimeMoment = startTime() ? moment(startTime()).tz(timezone) : null;
  const endTimeMoment = endTime() ? moment(endTime()).tz(timezone) : null;
  const sameDay = startTimeMoment && endTimeMoment && startTimeMoment.isSame(endTimeMoment, 'date');

  const CAMPAIGN_MIN_NAME_LENGTH = 5;
  const CAMPAIGN_MAX_NAME_LENGTH = 100;
  const [minimumBrowseTime, setMinimumBrowseTime] = useState(5);

  const isEndTimeBeforeStartTime = (end: Moment, start: Moment) => {
    return end.isBefore(start);
  };

  const getType = (): string => {
    return campaign.type === 'cartAbandonment'
      ? t('campaigns:offsite.cartAbandonment.title')
      : t('campaigns:offsite.browseAbandonment.title');
  };

  const validationMessage =
    endTimeMoment &&
    startTimeMoment &&
    isEndTimeBeforeStartTime(endTimeMoment, startTimeMoment) &&
    t('campaigns:newCampaigns.details.time.validationMessage');

  const Sidebar = (
    <>
      <P bold={true}>
        Objective: <Span>{t('campaigns:objectives.recoverAbandonedSales.title')}</Span>
      </P>

      <P bold={true}>
        Feature: <Span>{getType()}</Span>
      </P>
    </>
  );

  const onNameChange = (name: string) => {
    dispatch({
      type: 'SET_NAME_VALUE',
      payload: { name },
    });
  };

  const onStartTimeChange = (date: Moment) => {
    dispatch({
      type: 'SET_START_DATE_VALUE',
      payload: { startTime: date },
    });
  };

  const onEndTimeChange = (date: Moment) => {
    dispatch({
      type: 'SET_END_DATE_VALUE',
      payload: { endTime: date },
    });
  };

  const onMinimumBrowseTimeChange = (value: string) => {
    setMinimumBrowseTime(Number(value));
  };

  const getValidationMessage = (value: number): string => {
    if (Number.isNaN(value) || value > 30) {
      return t('campaigns:newCampaigns.details.minimumBrowseTime.validationWarning');
    }
  };

  function isNameValid(): boolean {
    const n = name();
    return n !== undefined && n.length >= CAMPAIGN_MIN_NAME_LENGTH && n.length <= CAMPAIGN_MAX_NAME_LENGTH;
  }
  function areTimesValid() {
    return startTime() !== undefined && endTime() !== undefined
      ? !isEndTimeBeforeStartTime(moment(endTime()), moment(startTime()))
      : true;
  }

  useEffect(() => {
    const isValid = isNameValid() && areTimesValid();
    updateStepValidityAction(isValid, validSteps, currentStep, dispatch);
  }, [campaign, currentStep]);

  useEffect(() => {
    dispatch({
      type: 'SET_MINIMUM_BROWSE_TIME',
      payload: { minimumBrowseTime },
    });
  }, [minimumBrowseTime]);

  return (
    <SingleColumnLayout
      heading={t('campaigns:newCampaigns.details.heading')}
      showSidebar={true}
      sidebar={Sidebar}
      sidebarHeading={t('campaigns:newCampaigns.sidebar.heading')}
    >
      <Form>
        <Input
          key="CampaignName"
          label={t('campaigns:newCampaigns.details.campaignName.label')}
          ariaLabel={t('campaigns:newCampaigns.details.campaignName.ariaLabel')}
          inputId="CampaignNameInput"
          onChange={onNameChange}
          value={name() || ''}
          validationState={campaign && isNameValid() ? 'complete' : name() ? 'warning' : null}
          validationMessage={
            campaign && isNameValid()
              ? null
              : name()
              ? t('campaigns:newCampaigns.details.campaignName.validation.errorMessage')
              : null
          }
        />
        <FormGroup>
          <Box>
            <DateTimePicker
              time={startTimeMoment}
              onChange={onStartTimeChange}
              label={t('campaigns:dates.start.label')}
              datePlaceholder={t('campaigns:dates.start.datePlaceholder')}
              dateAriaLabel={t('campaigns:dates.start.dateLabel')}
              timeAriaLabel={t('campaigns:dates.start.timeLabel')}
              minTime="00:00"
              maxTime={sameDay ? endTimeMoment.format('HH:mm') : null}
              timezone={timezone}
            />
          </Box>
        </FormGroup>
        <FormGroup>
          <Box>
            <DateTimePicker
              time={endTimeMoment}
              onChange={onEndTimeChange}
              label={t('campaigns:dates.end.label')}
              datePlaceholder={t('campaigns:dates.end.datePlaceholder')}
              dateAriaLabel={t('campaigns:dates.end.dateLabel')}
              timeAriaLabel={t('campaigns:dates.end.timeLabel')}
              minTime={sameDay ? startTimeMoment.format('HH:mm') : null}
              maxTime="23:00"
              timezone={timezone}
              validationMessage={validationMessage}
            />
          </Box>
        </FormGroup>
        <FormGroup>
          {campaign.type === 'browseAbandonment' && (
            <Input
              key="MinimumBrowseTime"
              ariaLabel={t('campaigns:newCampaigns.details.minimumBrowseTime.label')}
              label={t('campaigns:newCampaigns.details.minimumBrowseTime.label')}
              value={minimumBrowseTime}
              type="number"
              onChange={onMinimumBrowseTimeChange}
              min="0"
              max="30"
              validationMessage={getValidationMessage(minimumBrowseTime)}
              validationState={getValidationMessage(minimumBrowseTime) ? 'warning' : 'complete'}
            />
          )}
        </FormGroup>
      </Form>
    </SingleColumnLayout>
  );
};
